import { useCallback, useEffect, useRef, useState } from 'react';
import {
  RESULT_HISTORY_INITIAL_DAYS,
  RESULT_HISTORY_PAGE_DAYS,
  resultHistoryArchiveCursor,
  resultHistoryCacheKey,
  resultHistoryTurnLimit,
  type ResultHistoryCursor,
} from '../core/lottery/resultHistory';
import type { ResultLike } from '../core/rules/officialDrawResults';
import { fetchResultHistoryPage } from '../services/results/fetchResultHistoryPage';
import {
  readResultHistoryPageCache,
  writeResultHistoryPageCache,
} from './resultHistoryPageCache';

function applyCachedOrFresh(
  enabled: boolean,
  cacheKey: string,
  weekFloorDayKey: string,
  cursorRef: { current: ResultHistoryCursor | null },
  hasMoreRef: { current: boolean },
  pageCountRef: { current: number },
  loadingRef: { current: boolean },
  keyRef: { current: string },
) {
  keyRef.current = cacheKey;
  loadingRef.current = false;
  if (!enabled) {
    cursorRef.current = null;
    hasMoreRef.current = false;
    pageCountRef.current = 0;
    return { pages: [] as ResultLike[][], hasMore: false };
  }
  const hit = readResultHistoryPageCache(cacheKey);
  if (hit) {
    cursorRef.current = hit.cursor;
    hasMoreRef.current = hit.hasMore;
    pageCountRef.current = hit.pages.length;
    return { pages: hit.pages, hasMore: hit.hasMore };
  }
  cursorRef.current = resultHistoryArchiveCursor(weekFloorDayKey);
  hasMoreRef.current = true;
  pageCountRef.current = 0;
  return { pages: [] as ResultLike[][], hasMore: true };
}

function initialPages(enabled: boolean, cacheKey: string): ResultLike[][] {
  if (!enabled) return [];
  return readResultHistoryPageCache(cacheKey)?.pages ?? [];
}

function initialHasMore(enabled: boolean, cacheKey: string): boolean {
  if (!enabled) return false;
  return readResultHistoryPageCache(cacheKey)?.hasMore ?? true;
}

function archivePageSize(firstPage: boolean): number {
  return resultHistoryTurnLimit(
    firstPage ? RESULT_HISTORY_INITIAL_DAYS : RESULT_HISTORY_PAGE_DAYS,
  );
}

export function useResultHistoryPages(opts: {
  enabled: boolean;
  platform: boolean;
  source: string | null | undefined;
  houseId: string;
  weekFloorDayKey: string;
}) {
  const { enabled, platform, source, houseId, weekFloorDayKey } = opts;
  const cacheKey = resultHistoryCacheKey({
    platform, source, houseId, weekFloorDayKey,
  });
  const [pages, setPages] = useState<ResultLike[][]>(() => initialPages(enabled, cacheKey));
  const [hasMore, setHasMore] = useState(() => initialHasMore(enabled, cacheKey));
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageError, setPageError] = useState(false);
  const loadingRef = useRef(false);
  const cursorRef = useRef<ResultHistoryCursor | null>(null);
  const hasMoreRef = useRef(enabled);
  const pageCountRef = useRef(0);
  const genRef = useRef(0);
  const keyRef = useRef(cacheKey);

  const loadMore = useCallback(async () => {
    if (!enabled || loadingRef.current || !hasMoreRef.current) return;
    const gen = genRef.current;
    const key = keyRef.current;
    loadingRef.current = true;
    setPageError(false);
    setLoadingMore(true);
    try {
      const firstArchivePage = pageCountRef.current === 0;
      const page = await fetchResultHistoryPage({
        platform,
        source,
        houseId,
        pageSize: archivePageSize(firstArchivePage),
        cursor: cursorRef.current,
        weekFloorDayKey,
      });
      if (gen !== genRef.current) return;
      const nextHasMore = page.hasMore && page.cursor != null;
      cursorRef.current = page.cursor;
      hasMoreRef.current = nextHasMore;
      setHasMore(nextHasMore);
      if (page.rows.length === 0) return;
      setPages((prev) => {
        const next = [...prev, page.rows];
        pageCountRef.current = next.length;
        writeResultHistoryPageCache(key, {
          pages: next,
          cursor: page.cursor,
          hasMore: nextHasMore,
        });
        return next;
      });
    } catch {
      if (gen !== genRef.current) return;
      setPageError(true);
    } finally {
      if (gen === genRef.current) {
        loadingRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [enabled, platform, source, houseId, weekFloorDayKey]);

  useEffect(() => {
    genRef.current += 1;
    setLoadingMore(false);
    const next = applyCachedOrFresh(
      enabled, cacheKey, weekFloorDayKey,
      cursorRef, hasMoreRef, pageCountRef, loadingRef, keyRef,
    );
    setPages(next.pages);
    setHasMore(next.hasMore);
    setPageError(false);
    return undefined;
  }, [enabled, cacheKey, weekFloorDayKey]);

  return { pages, hasMore: enabled ? hasMore : false, loadingMore, pageError: enabled ? pageError : false, loadMore };
}
